import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { TableNumber } from 'utilities';
import {
  CRUDLayout,
  InputSearch,
  CreateButton,
  DeleteButton,
  UpdateButton,
  ActionButton,
  DataStatus,
  CreateModal,
  UpdateModal,
  DeleteModal,
  Alert,
  Input,
  Pagination,
  TextArea,
  Switch,
  THead,
  TBody,
  ThFixed,
  TdFixed,
  Tr,
  Th,
  Td,
  SelectSearch,
} from 'components';
import { StatusKaryawanApi } from 'api';
import { useIsGuest } from 'hooks';

const StatusKaryawan = ({ setNavbarTitle }) => {
  const title = 'Status Pekerja';
  const guest = useIsGuest();

  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchKey, setSearchKey] = useState('');
  const [isCreateForm, setIsCreateForm] = useState(false);
  const [isUpdateform, setIsUpdateform] = useState(false);
  const [isDeleteData, setIsDeleteData] = useState(false);
  // data jenis anggaran
  const [data, setData] = useState([]);
  const [dataJenis, setDataJenis] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [dataLength, setDataLength] = useState(10);
  const [dataCount, setDataCount] = useState(0);
  const [updateData, setUpdateData] = useState([]);
  const [deleteData, setDeleteData] = useState([]);
  const [alertConfig, setAlertConfig] = useState({
    show: false,
    variant: 'danger',
    text: '',
    searchKey: '',
  });

  // request data dari server
  const getData = () => {
    setIsPageLoading(true);
    setIsSearching(false);
    axios
      .all([
        StatusKaryawanApi.getPage(page, dataLength, searchKey),
        StatusKaryawanApi.getDropdownJenis(),
      ])
      .then(
        axios.spread((res, jenis) => {
          setData(res.data.data);
          setTotalPage(res.data.total_page);
          setDataCount(res.data.data_count);
          setDataJenis(jenis.data.data);
        })
      )
      .catch(() =>
        setAlertConfig({
          show: true,
          variant: 'danger',
          text: 'Data gagal dimuat!',
        })
      )
      .finally(() => {
        setIsPageLoading(false);
      });
  };

  // request search data dari server
  const searchData = (e) => {
    // mencegah terjadinya page reload
    e.preventDefault();

    // set loading
    setIsPageLoading(true);

    // nilai dari form search
    const key = searchKey;

    StatusKaryawanApi.search(key)
      .then((res) => {
        setData(res.data.data);
        setTotalPage(res.data.total_page);
        setAlertConfig({
          variant: 'primary',
          searchKey: key,
        });
      })
      .catch(() => {
        setAlertConfig({
          variant: 'danger',
          text: 'Gagal untuk memuat data',
        });
      })
      .finally(() => {
        setPage(1);
        setIsSearching(true);
        setIsPageLoading(false);
      });
  };

  // menangani hide/show data
  const changeDataStatus = (status, id) => {
    setIsPageLoading(true);

    const value = {
      id_status_pekerja: id,
    };

    const onLoadedSuccess = () => {
      setIsSearching(false);
      setAlertConfig({
        show: true,
        variant: 'primary',
        text: 'Ubah status data berhasil',
      });
    };

    const onLoadedFailed = () => {
      setIsSearching(false);
      setAlertConfig({
        show: true,
        variant: 'danger',
        text: 'Ubah status data gagal',
      });
    };

    status === true
      ? StatusKaryawanApi.show(value)
          .then(() => onLoadedSuccess())
          .catch(() => onLoadedFailed())
          .finally(() => getData())
      : StatusKaryawanApi.hide(value)
          .then(() => onLoadedSuccess())
          .catch(() => onLoadedFailed())
          .finally(() => getData());
  };

  useEffect(() => {
    // set judul di Navbar
    setNavbarTitle(title);

    // jalankan function request data ke server
    getData();

    // menangani pembersihan saat fetching data
    return () => {
      setIsPageLoading(false);
    };
  }, [setNavbarTitle, page, dataLength, searchKey]);

  // skema validasi form
  const formValidationSchema = Yup.object().shape({
    id_jenis_pekerja: Yup.string().required('Jenis Pekerja wajib diisi'),
    kode_pekerja: Yup.string()
      .required('Kode Pekerja wajib diisi')
      .test({
        name: 'kode pekerja',
        message: 'Kode pekerja tidak boleh lebih dari 2 karakter',
        test: (val) => val?.length < 3,
      }),
    nama_status_pekerja: Yup.string().required(
      'Nama Status Pekerja wajib diisi'
    ),
  });

  // nilai awal form
  const formInitialValues = {
    id_status_pekerja: updateData.id_status_pekerja,
    id_jenis_pekerja: updateData.id_jenis_pekerja,
    nama_jenis_pekerja: updateData.nama_jenis_pekerja,
    kode_pekerja: updateData.kode_pekerja,
    nama_status_pekerja: updateData.nama_status_pekerja,
    keterangan: updateData.keterangan,
  };

  // modal tambah
  const TambahModal = ({ isCreateForm, setIsCreateForm }) => {
    const [formAlertConfig, setFormAlertConfig] = useState({
      show: false,
      variant: 'danger',
      text: '',
      // text: ["error not unique", "error already exists"]
    });

    const formSubmitHandler = (values, { setSubmitting }) => {
      StatusKaryawanApi.create(values)
        .then((res) => {
          setAlertConfig({
            show: true,
            variant: 'primary',
            text: 'Tambah data berhasil!',
          });
          setIsCreateForm(false);
          getData();
        })
        .catch(({ response }) => {
          console.log({ response });

          setFormAlertConfig({
            show: true,
            variant: 'danger',
            text: response?.data?.error ?? 'Tambah data gagal!',
          });
          setSubmitting(false);
        });
    };

    useEffect(() => {
      return () =>
        setFormAlertConfig({
          show: false,
          variant: 'danger',
          text: '',
        });
    }, []);

    return (
      <CreateModal
        show={isCreateForm}
        onHide={() => setIsCreateForm(false)}
        title={title}
      >
        {/* Form Alert */}
        <div className="mt-3 mx-3">
          <Alert
            show={formAlertConfig.show}
            variant={formAlertConfig.variant}
            text={
              Array.isArray(formAlertConfig.text) ? (
                <ul>
                  {' '}
                  {formAlertConfig.text.map((err) => (
                    <li>{err}</li>
                  ))}{' '}
                </ul>
              ) : (
                formAlertConfig.text
              )
            }
          />
        </div>

        <Formik
          initialValues={formInitialValues}
          validationSchema={formValidationSchema}
          onSubmit={formSubmitHandler}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit}>
              <Modal.Body>
                <SelectSearch
                  label="Jenis Pekerja"
                  name="id_jenis_pekerja"
                  defaultValue={{
                    label: updateData.nama_jenis_pekerja
                      ? updateData.nama_jenis_pekerja
                      : 'Pilih Jenis Pekerja',
                    value: updateData.id_jenis_pekerja
                      ? updateData.id_jenis_pekerja
                      : '',
                  }}
                  onChange={(val) => {
                    setFieldValue('id_jenis_pekerja', val.value);
                  }}
                  option={dataJenis.map((val) => {
                    return {
                      value: val.id_jenis_pekerja,
                      label: val.nama_jenis_pekerja,
                    };
                  })}
                  error={
                    errors.id_jenis_pekerja && touched.id_jenis_pekerja && true
                  }
                  errorText={
                    errors.id_jenis_pekerja &&
                    touched.id_jenis_pekerja &&
                    errors.id_jenis_pekerja
                  }
                />

                <Input
                  label="Kode Pekerja"
                  name="kode_pekerja"
                  value={values.kode_pekerja}
                  error={errors.kode_pekerja && touched.kode_pekerja && true}
                  errorText={errors.kode_pekerja}
                  onChange={handleChange}
                />

                <Input
                  label="Status Pekerja"
                  name="nama_status_pekerja"
                  value={values.nama_status_pekerja}
                  error={
                    errors.nama_status_pekerja &&
                    touched.nama_status_pekerja &&
                    true
                  }
                  errorText={errors.nama_status_pekerja}
                  onChange={handleChange}
                />

                <TextArea
                  label="Keterangan"
                  name="keterangan"
                  value={values.keterangan}
                  error={errors.keterangan && touched.keterangan && true}
                  errorText={errors.keterangan}
                  onChange={handleChange}
                />
              </Modal.Body>
              <Modal.Footer>
                <ActionButton
                  type="submit"
                  variant="primary"
                  text="Tambah Data"
                  loading={isSubmitting}
                />
              </Modal.Footer>
            </form>
          )}
        </Formik>
      </CreateModal>
    );
  };

  // modal ubah
  const UbahModal = ({ isUpdateform, setIsUpdateform }) => {
    const [formAlertConfig, setFormAlertConfig] = useState({
      show: false,
      variant: 'danger',
      text: '',
      // text: ["error not unique", "error already exists"]
    });

    const formSubmitHandler = (values, { setSubmitting }) => {
      const finalValues = {
        id_status_karyawan: updateData.id_status_karyawan,
        ...values,
      };

      StatusKaryawanApi.update(finalValues)
        .then(() => {
          // konfigurasi alert
          setAlertConfig({
            show: true,
            variant: 'primary',
            text: 'Ubah data berhasil!',
          });

          setIsUpdateform(false);
          getData();
        })
        .catch(({ response }) => {
          console.log({ response });

          setFormAlertConfig({
            show: true,
            variant: 'danger',
            text: response?.data?.error ?? 'Ubah data gagal!',
          });
          setSubmitting(false);
        });
    };

    useEffect(() => {
      return () =>
        setFormAlertConfig({
          show: false,
          variant: 'danger',
          text: '',
        });
    }, []);

    return (
      <UpdateModal
        show={isUpdateform}
        onHide={() => setIsUpdateform(false)}
        title={title}
      >
        {/* Form Alert */}
        <div className="mt-3 mx-3">
          <Alert
            show={formAlertConfig.show}
            variant={formAlertConfig.variant}
            text={
              Array.isArray(formAlertConfig.text) ? (
                <ul>
                  {' '}
                  {formAlertConfig.text.map((err) => (
                    <li>{err}</li>
                  ))}{' '}
                </ul>
              ) : (
                formAlertConfig.text
              )
            }
          />
        </div>

        <Formik
          initialValues={formInitialValues}
          validationSchema={formValidationSchema}
          onSubmit={formSubmitHandler}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit}>
              <Modal.Body>
                <SelectSearch
                  label="Jenis Pekerja"
                  name="id_jenis_pekerja"
                  defaultValue={{
                    label: updateData.nama_jenis_pekerja
                      ? updateData.nama_jenis_pekerja
                      : 'Pilih Jenis Pekerja',
                    value: updateData.id_jenis_pekerja
                      ? updateData.id_jenis_pekerja
                      : '',
                  }}
                  onChange={(val) => {
                    setFieldValue('id_jenis_pekerja', val.value);
                  }}
                  option={dataJenis.map((val) => {
                    return {
                      value: val.id_jenis_pekerja,
                      label: val.nama_jenis_pekerja,
                    };
                  })}
                  error={
                    errors.id_jenis_pekerja && touched.id_jenis_pekerja && true
                  }
                  errorText={
                    errors.id_jenis_pekerja &&
                    touched.id_jenis_pekerja &&
                    errors.id_jenis_pekerja
                  }
                />

                <Input
                  label="Kode Pekerja"
                  name="kode_pekerja"
                  value={values.kode_pekerja}
                  error={errors.kode_pekerja && touched.kode_pekerja && true}
                  errorText={errors.kode_pekerja}
                  onChange={handleChange}
                />

                <Input
                  label="Status Pekerja"
                  name="nama_status_pekerja"
                  value={values.nama_status_pekerja}
                  error={
                    errors.nama_status_pekerja &&
                    touched.nama_status_pekerja &&
                    true
                  }
                  errorText={errors.nama_status_pekerja}
                  onChange={handleChange}
                />

                <TextArea
                  label="Keterangan"
                  name="keterangan"
                  value={values.keterangan}
                  error={errors.keterangan && touched.keterangan && true}
                  errorText={errors.keterangan}
                  onChange={handleChange}
                />
              </Modal.Body>
              <Modal.Footer>
                <ActionButton
                  type="submit"
                  variant="success"
                  text="Ubah Data"
                  loading={isSubmitting}
                />
              </Modal.Footer>
            </form>
          )}
        </Formik>
      </UpdateModal>
    );
  };

  // modal hapus
  const HapusModal = () => {
    // set id dari data yang ingin dihapus
    const values = { id_status_pekerja: deleteData.id_status_pekerja };

    // menangani delete button loading
    const [btnLoading, setBtnLoading] = useState(false);

    // menangani pembersihan saat fetching data
    useEffect(() => {
      return () => {
        setBtnLoading(false);
      };
    }, []);

    // request hapus data ke server
    const deleteDataHandler = () => {
      // set delete button loading
      setBtnLoading(true);

      StatusKaryawanApi.delete(values)
        .then(() => {
          // konfigurasi alert
          setAlertConfig({
            show: true,
            variant: 'primary',
            text: 'Hapus data berhasil!',
          });
        })
        .catch((err) => {
          // konfigurasi alert
          setAlertConfig({
            show: true,
            variant: 'danger',
            text: `Hapus data gagal! (${err})`,
          });
        })
        .finally(() => {
          // menutup modal
          setIsDeleteData(false);
          // request data baru ke server
          getData();
        });
    };

    return (
      <DeleteModal
        show={isDeleteData}
        onHide={() => setIsDeleteData(false)}
        loading={btnLoading}
        onConfirm={deleteDataHandler}
        title={title}
      >
        <span>Nama Status Pekerja : {deleteData.nama_status_pekerja}</span>
      </DeleteModal>
    );
  };

  // Tabel
  const Table = () => (
    <>
      <CRUDLayout.Table>
        <THead>
          <Tr>
            <ThFixed>No</ThFixed>
            {guest ? '' : <ThFixed>Aksi</ThFixed>}
            <Th style={{ width: '10px', whiteSpace: 'nowrap' }}>Kode Status</Th>
            <Th>Jenis Pekerja</Th>
            <Th>Status Pekerja</Th>
            <Th>Keterangan</Th>
          </Tr>
        </THead>
        <TBody>
          {data.map((val, index) => (
            <Tr key={index}>
              <TdFixed>{TableNumber(page, dataLength, index)}</TdFixed>
              {guest ? (
                ''
              ) : (
                <TdFixed>
                  <div className="d-flex justify-content-center">
                    <UpdateButton
                      onClick={() => {
                        setUpdateData(val);
                        setIsUpdateform(true);
                      }}
                    />
                    <DeleteButton
                      onClick={() => {
                        setDeleteData(val);
                        setIsDeleteData(true);
                      }}
                    />
                    <Switch
                      id={toString(index + 1)}
                      checked={!val.is_hidden}
                      onChange={() =>
                        changeDataStatus(val.is_hidden, val.id_status_pekerja)
                      }
                    />
                  </div>
                </TdFixed>
              )}
              <Td>{val.kode_pekerja ?? ''}</Td>
              <Td>{val.nama_jenis_pekerja}</Td>
              <Td>{val.nama_status_pekerja}</Td>
              <Td>{val.keterangan}</Td>
            </Tr>
          ))}
        </TBody>
      </CRUDLayout.Table>
      {!isSearching && (
        <Pagination
          dataLength={dataLength}
          dataNumber={page * dataLength - dataLength + 1}
          dataPage={dataCount < dataLength ? dataCount : page * dataLength}
          dataCount={dataCount}
          onDataLengthChange={(e) => {
            setDataLength(e.target.value);
            setPage(1);
          }}
          currentPage={page}
          totalPage={totalPage}
          onPaginationChange={({ selected }) => setPage(selected + 1)}
        />
      )}
    </>
  );

  return (
    <CRUDLayout>
      {/* head */}
      <CRUDLayout.Head>
        {/* search section */}
        <CRUDLayout.HeadSearchSection>
          <div className="d-flex mb-3">
            <InputSearch
              onChange={(e) => {
                const key = e.target.value;
                setSearchKey(key);
                setAlertConfig({
                  show: key ? true : false,
                  variant: 'primary',
                  text: 'Hasil dari pencarian: ' + key,
                });
              }}
            />
          </div>
        </CRUDLayout.HeadSearchSection>

        {/* button section */}
        <CRUDLayout.HeadButtonSection>
          <CreateButton
            onClick={() => {
              setUpdateData({
                id_status_pekerja: '',
                id_jenis_pekerja: '',
                nama_jenis_pekerja: '',
                kode_pekerja: '',
                nama_status_pekerja: '',
                keterangan: '',
              });
              setIsCreateForm(true);
            }}
          />
        </CRUDLayout.HeadButtonSection>
      </CRUDLayout.Head>

      {/* aLert */}
      <Alert
        show={alertConfig.show}
        variant={alertConfig.variant}
        text={alertConfig.text}
        showCloseButton={true}
        onClose={() =>
          setAlertConfig({
            ...alertConfig,
            show: false,
          })
        }
      />

      {/* table section */}
      {
        // cek apakah data sedang dimuat (loading)
        isPageLoading === true ? (
          // loading
          <DataStatus loading={true} text="Memuat data..." />
        ) : // Cek apakah ada data
        data.length > 0 ? (
          // Ada data
          <Table />
        ) : (
          // Tidak ada data
          <DataStatus text="Tidak ada data" />
        )
      }

      {/* Modal */}
      {isCreateForm && (
        <TambahModal
          isCreateForm={isCreateForm}
          setIsCreateForm={setIsCreateForm}
        />
      )}
      {isUpdateform && (
        <UbahModal
          isUpdateform={isUpdateform}
          setIsUpdateform={setIsUpdateform}
        />
      )}
      <HapusModal />
    </CRUDLayout>
  );
};

export default StatusKaryawan;
